'use client'

import { useState } from 'react'
import { colorForName, initialsFor } from './reviewsFormat'

export type ReviewerAvatarProps = {
  src?: string | null
  name: string
  /** Size + font classes for the circle, e.g. 'size-16 text-2xl'. */
  sizeClass?: string
  /** How many initials to show in the fallback (1 for the wall). */
  initialsMax?: number
  ring?: boolean
}

/**
 * Reviewer avatar: shows the photo when present and loadable, otherwise a
 * deterministic colored initials circle (colorForName). On image load error
 * it falls back to initials. Decorative — `aria-hidden` (the card already
 * names the reviewer in text).
 */
export function ReviewerAvatar({
  src,
  name,
  sizeClass = 'size-16 text-2xl',
  initialsMax = 1,
  ring = true,
}: ReviewerAvatarProps) {
  const [failed, setFailed] = useState(false)
  const ringCls = ring ? 'ring-2 ring-white shadow-[0_2px_10px_rgba(0,0,0,0.12)]' : ''
  if (src && !failed) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- external/Supabase URL; next/image domain config not wired in shared-ui
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        className={`${sizeClass} rounded-full object-cover shrink-0 bg-[var(--color-bg-secondary)] ${ringCls}`}
      />
    )
  }
  return (
    <span
      aria-hidden="true"
      className={`${sizeClass} rounded-full shrink-0 flex items-center justify-center text-white font-semibold ${ringCls}`}
      style={{ backgroundColor: colorForName(name) }}
    >
      {initialsFor(name, initialsMax)}
    </span>
  )
}
